// frontend/src/pages/admin/StockIn.js
import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiPlus,
  FiTrash2,
  FiSave,
  FiX,
  FiEye,
  FiCalendar,
  FiPackage,
  FiSearch
} from 'react-icons/fi';

const StockIn = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    supplier: '',
    notes: '',
    items: []
  });

  // Product search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchInputRef = useRef(null);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Date filter
  const [dateFilter, setDateFilter] = useState({
    start_date: '',
    end_date: ''
  });

  // ========================================
  // FETCH DATA
  // ========================================
  useEffect(() => {
    fetchTransactions();
    fetchProducts();
  }, [pagination.page, dateFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/inventory/stock-in', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          start_date: dateFilter.start_date,
          end_date: dateFilter.end_date
        }
      });

      if (response.data.success) {
        setTransactions(response.data.data.transactions);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch transactions error:', error);
      toast.error('Орлого татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products', {
        params: { page: 1, limit: 1000 }
      });

      if (response.data.success) {
        setProducts(response.data.data.products);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  };

  // ========================================
  // PRODUCT SEARCH
  // ========================================
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const results = products.filter(p =>
      p.barcode.toLowerCase().includes(query.toLowerCase()) ||
      p.name.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10); // Top 10 results

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleSelectProduct = (product) => {
    // Check if product already added
    const existingIndex = formData.items.findIndex(
      item => item.product_id === product.id
    );

    if (existingIndex >= 0) {
      toast.warning('Бараа аль хэдийн нэмэгдсэн байна');
      setSearchQuery('');
      setShowSearchResults(false);
      return;
    }

    // Add product with cost_price as default unit_price
    const item = {
      product_id: product.id,
      product_name: product.name,
      product_barcode: product.barcode,
      unit: product.unit,
      stock_quantity: product.stock_quantity, // Current stock
      quantity: 1, // Default quantity
      unit_price: product.cost_price || 0, // Cost price as default
      total_price: product.cost_price || 0
    };

    setFormData({
      ...formData,
      items: [...formData.items, item]
    });

    toast.success('Бараа нэмэгдлээ');
    setSearchQuery('');
    setShowSearchResults(false);
    
    // Focus back to search
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // ========================================
  // ITEM HANDLERS
  // ========================================
  const handleQuantityChange = (index, newQuantity) => {
    const updatedItems = [...formData.items];
    const qty = parseInt(newQuantity) || 0;

    updatedItems[index].quantity = qty;
    updatedItems[index].total_price = qty * updatedItems[index].unit_price;

    setFormData({ ...formData, items: updatedItems });
  };

  const handlePriceChange = (index, newPrice) => {
    const updatedItems = [...formData.items];
    const price = parseFloat(newPrice) || 0;

    updatedItems[index].unit_price = price;
    updatedItems[index].total_price = updatedItems[index].quantity * price;

    setFormData({ ...formData, items: updatedItems });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
    toast.info('Бараа хасагдлаа');
  };

  // ========================================
  // SUBMIT
  // ========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.warning('Барааны жагсаалт хоосон байна');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/inventory/stock-in', formData);

      if (response.data.success) {
        toast.success('Орлого амжилттай бүртгэгдлээ');
        setShowCreateModal(false);
        resetForm();
        fetchTransactions();
      }
    } catch (error) {
      console.error('Create stock in error:', error);
      toast.error(error.response?.data?.message || 'Орлого бүртгэхэд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0],
      supplier: '',
      notes: '',
      items: []
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const viewDetail = async (transaction) => {
    try {
      const response = await axios.get(`/api/inventory/transactions/${transaction.id}`);
      
      if (response.data.success) {
        setSelectedTransaction(response.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Fetch detail error:', error);
      toast.error('Дэлгэрэнгүй татахад алдаа гарлаа');
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total_price, 0);
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Барааны орлого</h2>
          <p className="text-gray-600 text-sm mt-1">Агуулахад бараа орж ирэх</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <FiPlus className="mr-2" />
          Орлого бүртгэх
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiCalendar className="inline mr-1" />
              Эхлэх огноо
            </label>
            <input
              type="date"
              value={dateFilter.start_date}
              onChange={(e) => setDateFilter({ ...dateFilter, start_date: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FiCalendar className="inline mr-1" />
              Дуусах огноо
            </label>
            <input
              type="date"
              value={dateFilter.end_date}
              onChange={(e) => setDateFilter({ ...dateFilter, end_date: e.target.value })}
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setDateFilter({ start_date: '', end_date: '' })}
              className="btn btn-secondary w-full"
            >
              Цэвэрлэх
            </button>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner-lg"></div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-th">Баримт №</th>
                <th className="table-th">Огноо</th>
                <th className="table-th">Нийлүүлэгч</th>
                <th className="table-th text-right">Нийт дүн</th>
                <th className="table-th">Үүсгэсэн</th>
                <th className="table-th text-right">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-500">
                    <FiPackage size={48} className="mx-auto mb-3 opacity-50" />
                    <p>Орлого олдсонгүй</p>
                  </td>
                </tr>
              ) : (
                transactions.map(trans => (
                  <tr key={trans.id} className="table-row">
                    <td className="table-td">
                      <span className="font-mono font-semibold text-green-600">
                        {trans.transaction_number}
                      </span>
                    </td>
                    <td className="table-td">{trans.transaction_date}</td>
                    <td className="table-td">{trans.supplier || '-'}</td>
                    <td className="table-td text-right font-semibold">
                      {trans.total_amount.toLocaleString()}₮
                    </td>
                    <td className="table-td">{trans.created_by_name}</td>
                    <td className="table-td text-right">
                      <button
                        onClick={() => viewDetail(trans)}
                        className="text-blue-600 hover:text-blue-700"
                        title="Дэлгэрэнгүй"
                      >
                        <FiEye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination-container">
          <p className="pagination-info">
            Нийт {pagination.total} орлого
          </p>
          <div className="pagination-controls">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              Өмнөх
            </button>
            <span className="pagination-page-info">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pages}
              className="pagination-button"
            >
              Дараах
            </button>
          </div>
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreateModal && (
        <CreateStockInModal
          formData={formData}
          setFormData={setFormData}
          searchQuery={searchQuery}
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          searchInputRef={searchInputRef}
          loading={loading}
          onSearch={handleSearch}
          onSelectProduct={handleSelectProduct}
          onQuantityChange={handleQuantityChange}
          onPriceChange={handlePriceChange}
          onRemoveItem={handleRemoveItem}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          calculateTotal={calculateTotal}
        />
      )}

      {/* DETAIL MODAL */}
      {showDetailModal && selectedTransaction && (
        <DetailModal
          transaction={selectedTransaction}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

// ========================================
// CREATE MODAL
// ========================================
const CreateStockInModal = ({
  formData,
  setFormData,
  searchQuery,
  searchResults,
  showSearchResults,
  searchInputRef,
  loading,
  onSearch,
  onSelectProduct,
  onQuantityChange,
  onPriceChange,
  onRemoveItem,
  onSubmit,
  onClose,
  calculateTotal
}) => (
  <div className="modal-overlay">
    <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="modal-header sticky top-0 bg-white z-10">
        <h3 className="modal-title">📥 Барааны орлого бүртгэх</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>

      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {/* Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Огноо <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.transaction_date}
                onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Нийлүүлэгч
              </label>
              <input
                type="text"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                placeholder="Нийлүүлэгчийн нэр"
                className="input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Тайлбар
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Нэмэлт тайлбар"
                className="input"
                rows="2"
              ></textarea>
            </div>
          </div>

          {/* Product Search */}
          <div className="bg-green-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">
              <FiSearch className="inline mr-1" />
              Бараа хайх
            </h4>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Баркод эсвэл нэрээр хайх..."
                className="input pl-10"
                autoComplete="off"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      onClick={() => onSelectProduct(product)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.barcode}</p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm font-semibold text-gray-900">
                            Үлдэгдэл: <span className="text-blue-600">{product.stock_quantity}</span>
                          </p>
                          <p className="text-xs text-gray-600">
                            Өртөг: {product.cost_price?.toLocaleString()}₮
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {showSearchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-gray-500">
                  Бараа олдсонгүй
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              💡 Enter дарж хайлт хийх эсвэл баркод scanner ашиглана уу
            </p>
          </div>

          {/* Items List */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">
              Барааны жагсаалт ({formData.items.length})
            </h4>
            
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <FiPackage size={48} className="mx-auto mb-2 opacity-50" />
                <p>Бараа нэмээгүй байна</p>
                <p className="text-xs mt-1">Дээрх хайлтаар бараа нэмнэ үү</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Бараа</th>
                      <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Одоогийн үлдэгдэл</th>
                      <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Орох тоо</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Үнэ</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Дүн</th>
                      <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Үйлдэл</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.product_barcode}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm text-blue-600 font-semibold">
                            {item.stock_quantity}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">{item.unit}</span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => onQuantityChange(index, e.target.value)}
                            className="input text-center w-20"
                            min="1"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => onPriceChange(index, e.target.value)}
                            className="input text-right w-32"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-600">
                          {item.total_price.toLocaleString()}₮
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => onRemoveItem(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2">
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-right font-bold text-gray-900">
                        НИЙТ ДҮН:
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-600 text-lg">
                        {calculateTotal().toLocaleString()}₮
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Болих
          </button>
          <button
            type="submit"
            disabled={loading || formData.items.length === 0}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <div className="spinner mr-2"></div>
                Хадгалж байна...
              </>
            ) : (
              <>
                <FiSave className="mr-2" />
                Хадгалах
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
);

// ========================================
// DETAIL MODAL
// ========================================
const DetailModal = ({ transaction, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content max-w-4xl">
      <div className="modal-header">
        <h3 className="modal-title">
          📥 Орлогын дэлгэрэнгүй - {transaction.transaction.transaction_number}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FiX size={24} />
        </button>
      </div>

      <div className="modal-body">
        {/* Header Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b">
          <div>
            <p className="text-sm text-gray-600">Огноо</p>
            <p className="font-semibold">{transaction.transaction.transaction_date}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Нийлүүлэгч</p>
            <p className="font-semibold">{transaction.transaction.supplier || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Үүсгэсэн</p>
            <p className="font-semibold">{transaction.transaction.created_by_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Төлөв</p>
            <span className="badge badge-success">Баталгаажсан</span>
          </div>
        </div>

        {/* Items */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Барааны жагсаалт</h4>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Бараа</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold">Тоо</th>
                  <th className="px-4 py-2 text-right text-sm font-semibold">Үнэ</th>
                  <th className="px-4 py-2 text-right text-sm font-semibold">Дүн</th>
                </tr>
              </thead>
              <tbody>
                {transaction.items.map((item, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-3">
                      <p className="font-semibold">{item.product_name}</p>
                      <p className="text-xs text-gray-500">{item.barcode}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold">{item.quantity}</span>
                      <span className="text-xs text-gray-500 ml-1">{item.unit}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.unit_price.toLocaleString()}₮
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">
                      {item.total_price.toLocaleString()}₮
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-right font-bold">
                    НИЙТ ДҮН:
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-600 text-lg">
                    {transaction.transaction.total_amount.toLocaleString()}₮
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Notes */}
        {transaction.transaction.notes && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-semibold text-gray-700 mb-1">Тайлбар:</p>
            <p className="text-gray-900">{transaction.transaction.notes}</p>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button onClick={onClose} className="btn btn-secondary w-full">
          Хаах
        </button>
      </div>
    </div>
  </div>
);

export default StockIn;